import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import {
  Box, Typography, CircularProgress, Alert, Chip, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Snackbar
} from '@mui/material'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import EventIcon from '@mui/icons-material/Event'
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import ExploreIcon from '@mui/icons-material/Explore'
import PersonIcon from '@mui/icons-material/Person'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import EditCalendarIcon from '@mui/icons-material/EditCalendar'
import DoneAllIcon from '@mui/icons-material/DoneAll'
import { format, formatDistanceToNow } from 'date-fns'
import {
  fetchAppointments, acceptAppointment, declineAppointment,
  completeAppointment, rescheduleAppointment,
  applyAppointmentUpdate, addAppointment
} from './appointmentsActions'
import useAppointmentsWebSocket from '../../hooks/useAppointmentsWebSocket'
import BottomNavBar from '../../components/BottomNavBar'
import PageHeader from '../../components/PageHeader'
import SegmentedTabs from '../../components/SegmentedTabs'

const statusConfig = {
  Completed: { color: '#21d07a' },
  Cancelled: { color: '#fd5068' },
  Accepted: { color: '#FF385C' },
  Pending: { color: '#ffa726' },
}

function getStatus(appointment) {
  return appointment.is_completed
    ? 'Completed'
    : appointment.is_cancelled
    ? 'Cancelled'
    : appointment.is_accepted
    ? 'Accepted'
    : 'Pending'
}

function StatusChip({ status }) {
  const c = statusConfig[status]?.color || '#999'
  return (
    <Chip
      label={status}
      size="small"
      sx={{
        fontWeight: 700,
        fontSize: '0.7rem',
        height: 22,
        borderRadius: 6,
        bgcolor: `${c}1a`,
        color: c,
        flexShrink: 0,
      }}
    />
  )
}

// ─── DateBadge ────────────────────────────────────────────────────────────────
// Small gradient calendar tile — muted (grayscale) for cancelled/past visits.

function DateBadge({ date, muted }) {
  return (
    <Box sx={{
      width: 54,
      height: 58,
      borderRadius: 2.5,
      flexShrink: 0,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#fff',
      background: muted
        ? 'linear-gradient(135deg, #9e9e9e 0%, #757575 100%)'
        : 'linear-gradient(135deg, #FF385C 0%, #FF6B83 100%)',
    }}>
      <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', lineHeight: 1 }}>
        {date ? format(date, 'MMM') : '--'}
      </Typography>
      <Typography sx={{ fontSize: '1.35rem', fontWeight: 800, lineHeight: 1.15 }}>
        {date ? format(date, 'd') : '?'}
      </Typography>
    </Box>
  )
}

// ─── AppointmentCard ────────────────────────────────────────────────────────────
// My own outgoing visit requests — read-only.

function AppointmentCard({ appointment }) {
  const status = getStatus(appointment)
  const date = appointment.scheduled_time ? new Date(appointment.scheduled_time) : null
  const muted = status === 'Cancelled' || status === 'Completed'

  return (
    <Box sx={{
      display: 'flex',
      gap: 1.5,
      mb: 1.5,
      bgcolor: 'background.paper',
      borderRadius: 3,
      p: 1.75,
      border: '1px solid',
      borderColor: 'divider',
      transition: 'box-shadow 0.2s, transform 0.2s',
      '&:hover': { boxShadow: '0 6px 20px rgba(0,0,0,0.10)', transform: 'translateY(-1px)' },
    }}>
      <DateBadge date={date} muted={muted} />

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.9375rem', minWidth: 0 }} noWrap>
            {appointment.post_title || 'Appointment'}
          </Typography>
          <StatusChip status={status} />
        </Box>

        {appointment.post_price != null && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <AttachMoneyIcon sx={{ fontSize: 15, color: 'primary.main', mr: 0.25 }} />
            <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 700, fontSize: '0.85rem' }}>
              {typeof appointment.post_price === 'number'
                ? appointment.post_price.toLocaleString('en-CA', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
                : appointment.post_price}
            </Typography>
          </Box>
        )}

        {appointment.post_address && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
            <LocationOnIcon sx={{ fontSize: 14, color: 'text.disabled', flexShrink: 0 }} />
            <Typography variant="caption" color="text.secondary" noWrap>
              {appointment.post_address}
            </Typography>
          </Box>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <EventIcon sx={{ fontSize: 14, color: 'text.disabled', flexShrink: 0 }} />
          <Typography variant="caption" color="text.secondary">
            {date ? date.toLocaleString() : '--'}
            {date && <> · {formatDistanceToNow(date, { addSuffix: true })}</>}
          </Typography>
        </Box>

        {appointment.notes && (
          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', display: 'block', mt: 0.5 }}>
            "{appointment.notes}"
          </Typography>
        )}

        {appointment.reschedule_reason && (
          <Typography variant="caption" sx={{ color: '#42a5f5', display: 'block', mt: 0.5 }}>
            <strong>Rescheduled:</strong> {appointment.reschedule_reason}
          </Typography>
        )}

        {status === 'Cancelled' && appointment.decline_reason && (
          <Typography variant="caption" sx={{ color: '#fd5068', display: 'block', mt: 0.5 }}>
            <strong>Declined:</strong> {appointment.decline_reason}
          </Typography>
        )}
      </Box>
    </Box>
  )
}

// ─── RequestCard ────────────────────────────────────────────────────────────────
// Incoming visit requests on a listing the current user owns — actionable.

function RequestCard({ appointment, busy, onAccept, onDecline, onComplete, onReschedule }) {
  const status = getStatus(appointment)
  const date = appointment.scheduled_time ? new Date(appointment.scheduled_time) : null
  const resolved = status === 'Cancelled' || status === 'Completed'
  const muted = resolved

  return (
    <Box sx={{
      display: 'flex',
      gap: 1.5,
      mb: 1.5,
      bgcolor: 'background.paper',
      borderRadius: 3,
      p: 1.75,
      border: '1px solid',
      borderColor: 'divider',
      transition: 'box-shadow 0.2s, transform 0.2s',
      '&:hover': { boxShadow: '0 6px 20px rgba(0,0,0,0.10)', transform: 'translateY(-1px)' },
    }}>
      <DateBadge date={date} muted={muted} />

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.9375rem', minWidth: 0 }} noWrap>
            {appointment.post_title || 'Your listing'}
          </Typography>
          <StatusChip status={status} />
        </Box>

        {appointment.requester_name && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
            <PersonIcon sx={{ fontSize: 14, color: 'text.disabled', flexShrink: 0 }} />
            <Typography variant="caption" color="text.secondary" noWrap>
              Requested by {appointment.requester_name}
            </Typography>
          </Box>
        )}

        {appointment.post_address && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
            <LocationOnIcon sx={{ fontSize: 14, color: 'text.disabled', flexShrink: 0 }} />
            <Typography variant="caption" color="text.secondary" noWrap>
              {appointment.post_address}
            </Typography>
          </Box>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <EventIcon sx={{ fontSize: 14, color: 'text.disabled', flexShrink: 0 }} />
          <Typography variant="caption" color="text.secondary">
            {date ? date.toLocaleString() : '--'}
            {date && <> · {formatDistanceToNow(date, { addSuffix: true })}</>}
          </Typography>
        </Box>

        {appointment.notes && (
          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', display: 'block', mt: 0.5 }}>
            "{appointment.notes}"
          </Typography>
        )}

        {appointment.reschedule_reason && (
          <Typography variant="caption" sx={{ color: '#42a5f5', display: 'block', mt: 0.5 }}>
            <strong>Rescheduled:</strong> {appointment.reschedule_reason}
          </Typography>
        )}

        {status === 'Cancelled' && appointment.decline_reason && (
          <Typography variant="caption" sx={{ color: '#fd5068', display: 'block', mt: 0.5 }}>
            <strong>Declined:</strong> {appointment.decline_reason}
          </Typography>
        )}

        {!resolved && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1.25 }}>
            {!appointment.is_accepted && (
              <Button
                size="small"
                variant="contained"
                disabled={busy}
                onClick={() => onAccept(appointment.id)}
                startIcon={<CheckIcon sx={{ fontSize: 16 }} />}
                sx={{ borderRadius: 10, fontSize: '0.75rem', bgcolor: '#21d07a', '&:hover': { bgcolor: '#1bb56a' } }}
              >
                Accept
              </Button>
            )}
            {appointment.is_accepted && (
              <Button
                size="small"
                variant="contained"
                disabled={busy}
                onClick={() => onComplete(appointment.id)}
                startIcon={<DoneAllIcon sx={{ fontSize: 16 }} />}
                sx={{ borderRadius: 10, fontSize: '0.75rem' }}
              >
                Mark Completed
              </Button>
            )}
            <Button
              size="small"
              variant="outlined"
              disabled={busy}
              onClick={() => onReschedule(appointment)}
              startIcon={<EditCalendarIcon sx={{ fontSize: 16 }} />}
              sx={{ borderRadius: 10, fontSize: '0.75rem' }}
            >
              Reschedule
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="error"
              disabled={busy}
              onClick={() => onDecline(appointment)}
              startIcon={<CloseIcon sx={{ fontSize: 16 }} />}
              sx={{ borderRadius: 10, fontSize: '0.75rem' }}
            >
              Decline
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  )
}

// ─── RescheduleDialog ───────────────────────────────────────────────────────────

function RescheduleDialog({ appointment, loading, onClose, onConfirm }) {
  const [value, setValue] = useState(null)
  const [reason, setReason] = useState('')

  useEffect(() => {
    setValue(appointment ? new Date(appointment.scheduled_time) : null)
    setReason('')
  }, [appointment])

  return (
    <Dialog open={!!appointment} onClose={loading ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Reschedule Visit</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Pick a new date and time for {appointment?.post_title || 'this visit'}.
        </Typography>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DateTimePicker
            label="New date & time"
            value={value}
            onChange={setValue}
            disablePast
            renderInput={(params) => <TextField {...params} fullWidth margin="normal" />}
          />
        </LocalizationProvider>
        <TextField
          label="Reason (optional)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          fullWidth
          multiline
          minRows={2}
          margin="normal"
          placeholder="e.g. Something came up, could we do an hour later instead?"
        />
      </DialogContent>
      <DialogActions sx={{ p: 2, pt: 0 }}>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button
          variant="contained"
          disabled={loading || !value}
          onClick={() => onConfirm(appointment.id, value.toISOString(), reason)}
          sx={{ borderRadius: 10 }}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// ─── DeclineDialog ──────────────────────────────────────────────────────────────

function DeclineDialog({ appointment, loading, onClose, onConfirm }) {
  const [reason, setReason] = useState('')

  useEffect(() => {
    setReason('')
  }, [appointment])

  return (
    <Dialog open={!!appointment} onClose={loading ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Decline Request</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Let {appointment?.requester_name || 'the requester'} know why you're declining the visit
          for {appointment?.post_title || 'this listing'} (optional).
        </Typography>
        <TextField
          label="Reason (optional)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          fullWidth
          multiline
          minRows={3}
          margin="normal"
          placeholder="e.g. That time no longer works, the unit is already rented, ..."
        />
      </DialogContent>
      <DialogActions sx={{ p: 2, pt: 0 }}>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button
          variant="contained"
          color="error"
          disabled={loading}
          onClick={() => onConfirm(appointment.id, reason)}
          sx={{ borderRadius: 10 }}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : 'Decline'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// ─── Empty state ──────────────────────────────────────────────────────────────

const emptyCopy = {
  upcoming: { title: 'No upcoming appointments', body: "Book a visit from a property you've liked", cta: true },
  past: { title: 'No past appointments', body: 'Completed and cancelled visits will show up here', cta: false },
  requests: { title: 'No appointment requests', body: 'Requests to visit your own listings will show up here', cta: false },
}

function EmptyState({ tab, onExplore }) {
  const copy = emptyCopy[tab]
  return (
    <Box sx={{
      bgcolor: 'background.paper',
      borderRadius: 3,
      p: 4,
      textAlign: 'center',
      border: '1.5px dashed',
      borderColor: 'divider',
    }}>
      <EventIcon sx={{ fontSize: 44, color: 'text.disabled', mb: 1.5 }} />
      <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 600 }}>
        {copy.title}
      </Typography>
      <Typography variant="body2" color="text.disabled" sx={{ mt: 0.5, mb: copy.cta ? 2.5 : 0 }}>
        {copy.body}
      </Typography>
      {copy.cta && (
        <Button
          variant="contained"
          onClick={onExplore}
          startIcon={<ExploreIcon />}
          sx={{
            borderRadius: 10,
            background: 'linear-gradient(135deg, #FF385C 0%, #FF6B83 100%)',
            boxShadow: '0 4px 14px rgba(255,56,92,0.3)',
            '&:hover': { background: 'linear-gradient(135deg, #D4003A 0%, #FF385C 100%)' },
          }}
        >
          Explore Listings
        </Button>
      )}
    </Box>
  )
}

const Appointments = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { loading, appointments, error } = useSelector(state => state.appointments || {})
  const { isAuthenticated } = useSelector(state => state.auth || {})
  const [tab, setTab] = useState('upcoming')
  const [actingId, setActingId] = useState(null)
  const [actionError, setActionError] = useState('')
  const [rescheduleTarget, setRescheduleTarget] = useState(null)
  const [declineTarget, setDeclineTarget] = useState(null)
  const [liveNotice, setLiveNotice] = useState('')

  useEffect(() => {
    dispatch(fetchAppointments())
  }, [dispatch])

  // Real-time updates: someone accepts/declines/reschedules/completes an
  // appointment you're party to, or books a new visit on your listing.
  const handleWsMessage = useCallback((type, data) => {
    if (type === 'appointment_created') {
      dispatch(addAppointment(data))
      setLiveNotice(`New visit request for ${data.post_title || 'your listing'}`)
    } else if (type === 'appointment_updated') {
      dispatch(applyAppointmentUpdate(data))
    }
  }, [dispatch])

  useAppointmentsWebSocket(isAuthenticated, handleWsMessage)

  const { upcoming, past, requests } = useMemo(() => {
    const list = appointments || []
    const isActuallyUpcoming = (a) => {
      if (a.is_cancelled || a.is_completed) return false
      if (typeof a.is_upcoming === 'boolean') return a.is_upcoming
      return a.scheduled_time ? new Date(a.scheduled_time) > new Date() : false
    }
    // "Requests" (is_owner) are split out entirely from my own outgoing
    // bookings — they get their own tab with moderation actions instead of
    // being mixed into Upcoming/Past.
    const mine = list.filter(a => !a.is_owner)
    const owned = list.filter(a => a.is_owner)

    const up = mine.filter(isActuallyUpcoming)
      .sort((a, b) => new Date(a.scheduled_time) - new Date(b.scheduled_time))
    const pastMine = mine.filter(a => !isActuallyUpcoming(a))
      .sort((a, b) => new Date(b.scheduled_time) - new Date(a.scheduled_time))
    // Needs-action requests float to the top; resolved ones settle below.
    const sortedRequests = [...owned].sort((a, b) => {
      const aPending = !a.is_cancelled && !a.is_completed
      const bPending = !b.is_cancelled && !b.is_completed
      if (aPending !== bPending) return aPending ? -1 : 1
      return new Date(a.scheduled_time) - new Date(b.scheduled_time)
    })

    return { upcoming: up, past: pastMine, requests: sortedRequests }
  }, [appointments])

  const activeList = tab === 'upcoming' ? upcoming : tab === 'past' ? past : requests

  const runAction = async (id, promise) => {
    setActionError('')
    setActingId(id)
    try {
      await promise
    } catch (e) {
      setActionError(e.message || 'Something went wrong')
    } finally {
      setActingId(null)
    }
  }

  const handleAccept = (id) => runAction(id, dispatch(acceptAppointment(id)))
  const handleComplete = (id) => runAction(id, dispatch(completeAppointment(id)))

  const handleRescheduleConfirm = async (id, iso, reason) => {
    setActionError('')
    setActingId(id)
    try {
      await dispatch(rescheduleAppointment(id, iso, reason))
      setRescheduleTarget(null)
    } catch (e) {
      setActionError(e.message || 'Failed to reschedule appointment')
    } finally {
      setActingId(null)
    }
  }

  const handleDeclineConfirm = async (id, reason) => {
    setActionError('')
    setActingId(id)
    try {
      await dispatch(declineAppointment(id, reason))
      setDeclineTarget(null)
    } catch (e) {
      setActionError(e.message || 'Failed to decline appointment')
    } finally {
      setActingId(null)
    }
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      <PageHeader title="Appointments" subtitle="Your scheduled property visits" />

      <SegmentedTabs
        value={tab}
        onChange={setTab}
        sx={{ mx: 2, mt: 2, mb: 2 }}
        tabs={[
          { key: 'upcoming', label: `Upcoming${upcoming.length > 0 ? ` (${upcoming.length})` : ''}` },
          { key: 'past', label: `Past${past.length > 0 ? ` (${past.length})` : ''}` },
          { key: 'requests', label: `Requests${requests.length > 0 ? ` (${requests.length})` : ''}` },
        ]}
      />

      {/* Scrollable content */}
      <Box sx={{ flex: 1, overflowY: 'auto', bgcolor: 'background.default' }}>
        {/* Cards */}
        <Box sx={{ px: 2, pt: 2, pb: 'calc(80px + env(safe-area-inset-bottom, 0px))' }}>
          {loading && (
            <Box display="flex" justifyContent="center" alignItems="center" py={6}>
              <CircularProgress />
            </Box>
          )}
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {actionError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError('')}>
              {actionError}
            </Alert>
          )}

          {!loading && !error && activeList.length === 0 && (
            <EmptyState tab={tab} onExplore={() => navigate('/posts')} />
          )}

          {!loading && !error && tab !== 'requests' && activeList.map((appointment) => (
            <AppointmentCard key={appointment.id} appointment={appointment} />
          ))}

          {!loading && !error && tab === 'requests' && activeList.map((appointment) => (
            <RequestCard
              key={appointment.id}
              appointment={appointment}
              busy={actingId === appointment.id}
              onAccept={handleAccept}
              onDecline={setDeclineTarget}
              onComplete={handleComplete}
              onReschedule={setRescheduleTarget}
            />
          ))}
        </Box>
      </Box>

      <RescheduleDialog
        appointment={rescheduleTarget}
        loading={rescheduleTarget != null && actingId === rescheduleTarget.id}
        onClose={() => setRescheduleTarget(null)}
        onConfirm={handleRescheduleConfirm}
      />

      <DeclineDialog
        appointment={declineTarget}
        loading={declineTarget != null && actingId === declineTarget.id}
        onClose={() => setDeclineTarget(null)}
        onConfirm={handleDeclineConfirm}
      />

      <Snackbar
        open={!!liveNotice}
        autoHideDuration={4000}
        onClose={() => setLiveNotice('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        message={liveNotice}
        sx={{ mb: 'calc(64px + env(safe-area-inset-bottom, 0px))' }}
      />

      <BottomNavBar />
    </Box>
  )
}

export default Appointments
